/**
 * Probability distributions used by Resolve's statistical tests.
 *
 * Implemented from first principles so the worker bundle has no numeric
 * dependency:
 *
 * - log-gamma by the Lanczos approximation (g = 7, 9 terms);
 * - regularized incomplete gamma and beta functions by series and Lentz
 *   continued fractions (Numerical Recipes formulation);
 * - standard normal and Student-t tails from those functions;
 * - quantiles by bracketed bisection on the tail probability, which is
 *   monotone, so inversion cannot diverge.
 *
 * Accuracy is close to double precision for the arguments used here
 * (significance levels between 1e-12 and 0.5, any positive degrees of
 * freedom). Invalid arguments throw a RangeError rather than returning a
 * silently wrong critical value.
 */

const MAX_ITERATIONS = 1000;
const EPSILON = 1e-15;
const FLOAT_MIN = 1e-300;

const LANCZOS_G = 7;
const LANCZOS_COEFFICIENTS = [
  0.99999999999980993, 676.5203681218851, -1259.1392167224028,
  771.32342877765313, -176.61502916214059, 12.507343278686905,
  -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
];

const requireProbability = (name: string, p: number): void => {
  if (!Number.isFinite(p) || p <= 0 || p >= 1) {
    throw new RangeError(`${name} must be strictly between 0 and 1; got ${p}`);
  }
};

const requireDegreesOfFreedom = (df: number): void => {
  if (!Number.isFinite(df) || df <= 0) {
    throw new RangeError(`Degrees of freedom must be positive; got ${df}`);
  }
};

/** Natural logarithm of the gamma function for x > 0. */
export const logGamma = (x: number): number => {
  if (!Number.isFinite(x) || x <= 0) {
    throw new RangeError(`logGamma requires x > 0; got ${x}`);
  }
  if (x < 0.5) {
    // Reflection keeps the Lanczos series in its accurate range.
    return Math.log(Math.PI / Math.sin(Math.PI * x)) - logGamma(1 - x);
  }
  const z = x - 1;
  let series = LANCZOS_COEFFICIENTS[0];
  for (let i = 1; i < LANCZOS_COEFFICIENTS.length; i++) {
    series += LANCZOS_COEFFICIENTS[i] / (z + i);
  }
  const t = z + LANCZOS_G + 0.5;
  return (
    0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(series)
  );
};

// ============================================================================
// Incomplete gamma and beta functions
// ============================================================================

/** Lower regularized gamma P(a, x) by its power series (x < a + 1). */
const gammaSeries = (a: number, x: number): number => {
  let term = 1 / a;
  let sum = term;
  let ap = a;
  for (let n = 0; n < MAX_ITERATIONS; n++) {
    ap += 1;
    term *= x / ap;
    sum += term;
    if (Math.abs(term) < Math.abs(sum) * EPSILON) break;
  }
  return sum * Math.exp(-x + a * Math.log(x) - logGamma(a));
};

/** Upper regularized gamma Q(a, x) by continued fraction (x ≥ a + 1). */
const gammaContinuedFraction = (a: number, x: number): number => {
  let b = x + 1 - a;
  let c = 1 / FLOAT_MIN;
  let d = 1 / b;
  let h = d;
  for (let i = 1; i < MAX_ITERATIONS; i++) {
    const an = -i * (i - a);
    b += 2;
    d = an * d + b;
    if (Math.abs(d) < FLOAT_MIN) d = FLOAT_MIN;
    c = b + an / c;
    if (Math.abs(c) < FLOAT_MIN) c = FLOAT_MIN;
    d = 1 / d;
    const delta = d * c;
    h *= delta;
    if (Math.abs(delta - 1) < EPSILON) break;
  }
  return Math.exp(-x + a * Math.log(x) - logGamma(a)) * h;
};

/** Upper regularized incomplete gamma function Q(a, x). */
export const regularizedGammaQ = (a: number, x: number): number => {
  if (!(a > 0) || !(x >= 0)) {
    throw new RangeError(`regularizedGammaQ requires a > 0, x ≥ 0`);
  }
  if (x === 0) return 1;
  return x < a + 1 ? 1 - gammaSeries(a, x) : gammaContinuedFraction(a, x);
};

/** Continued fraction for the incomplete beta function (Lentz). */
const betaContinuedFraction = (x: number, a: number, b: number): number => {
  const qab = a + b;
  const qap = a + 1;
  const qam = a - 1;
  let c = 1;
  let d = 1 - (qab * x) / qap;
  if (Math.abs(d) < FLOAT_MIN) d = FLOAT_MIN;
  d = 1 / d;
  let h = d;
  for (let m = 1; m < MAX_ITERATIONS; m++) {
    const m2 = 2 * m;
    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < FLOAT_MIN) d = FLOAT_MIN;
    c = 1 + aa / c;
    if (Math.abs(c) < FLOAT_MIN) c = FLOAT_MIN;
    d = 1 / d;
    h *= d * c;
    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < FLOAT_MIN) d = FLOAT_MIN;
    c = 1 + aa / c;
    if (Math.abs(c) < FLOAT_MIN) c = FLOAT_MIN;
    d = 1 / d;
    const delta = d * c;
    h *= delta;
    if (Math.abs(delta - 1) < EPSILON) break;
  }
  return h;
};

/** Regularized incomplete beta function I_x(a, b). */
export const regularizedBeta = (x: number, a: number, b: number): number => {
  if (!(a > 0) || !(b > 0) || Number.isNaN(x)) {
    throw new RangeError(`regularizedBeta requires a > 0, b > 0`);
  }
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const logFront =
    logGamma(a + b) -
    logGamma(a) -
    logGamma(b) +
    a * Math.log(x) +
    b * Math.log1p(-x);
  // Use the continued fraction where it converges fastest; the symmetry
  // I_x(a, b) = 1 − I_{1−x}(b, a) covers the other side.
  if (x < (a + 1) / (a + b + 2)) {
    return (Math.exp(logFront) * betaContinuedFraction(x, a, b)) / a;
  }
  return 1 - (Math.exp(logFront) * betaContinuedFraction(1 - x, b, a)) / b;
};

// ============================================================================
// Quantile inversion
// ============================================================================

/**
 * Find x ≥ 0 where a decreasing tail function equals `target`.
 * Doubling brackets the root, then bisection converges unconditionally.
 */
const invertDecreasingTail = (
  tail: (x: number) => number,
  target: number
): number => {
  let lo = 0;
  let hi = 1;
  while (tail(hi) > target) {
    lo = hi;
    hi *= 2;
    if (hi > 1e300) {
      throw new RangeError(`Tail probability ${target} is out of range`);
    }
  }
  for (let i = 0; i < 2000 && hi - lo > 4 * Number.EPSILON * hi; i++) {
    const mid = 0.5 * (lo + hi);
    if (tail(mid) > target) lo = mid;
    else hi = mid;
  }
  return 0.5 * (lo + hi);
};

// ============================================================================
// Standard normal distribution
// ============================================================================

/** Upper tail P(Z > x) of the standard normal distribution. */
export const normalUpperTail = (x: number): number => {
  if (Number.isNaN(x)) return NaN;
  if (x === Infinity) return 0;
  if (x === -Infinity) return 1;
  const erfcHalf = (z: number) => 0.5 * regularizedGammaQ(0.5, z * z);
  return x >= 0 ? erfcHalf(x / Math.SQRT2) : 1 - erfcHalf(-x / Math.SQRT2);
};

/** Cumulative distribution P(Z ≤ x) of the standard normal distribution. */
export const normalCdf = (x: number): number => normalUpperTail(-x);

/** Standard normal quantile: the x with P(Z ≤ x) = p, for 0 < p < 1. */
export const normalQuantile = (p: number): number => {
  requireProbability('Probability', p);
  if (p === 0.5) return 0;
  return p > 0.5
    ? invertDecreasingTail(normalUpperTail, 1 - p)
    : -invertDecreasingTail(normalUpperTail, p);
};

/** Two-sided standard normal critical value z(1 − α/2). */
export const twoSidedNormalCriticalValue = (alpha: number): number => {
  requireProbability('Significance level', alpha);
  return invertDecreasingTail(normalUpperTail, alpha / 2);
};

// ============================================================================
// Student's t distribution
// ============================================================================

/** Two-sided tail P(|T| > t) of Student's t with `df` degrees of freedom. */
export const studentTTwoSidedTail = (t: number, df: number): number => {
  requireDegreesOfFreedom(df);
  if (Number.isNaN(t)) return NaN;
  const absT = Math.abs(t);
  if (absT === Infinity) return 0;
  return regularizedBeta(df / (df + absT * absT), df / 2, 0.5);
};

/** Cumulative distribution P(T ≤ t) of Student's t. */
export const studentTCdf = (t: number, df: number): number => {
  const half = 0.5 * studentTTwoSidedTail(t, df);
  return t >= 0 ? 1 - half : half;
};

/** Two-sided Student-t critical value t(1 − α/2, df). */
export const twoSidedStudentTCriticalValue = (
  alpha: number,
  df: number
): number => {
  requireProbability('Significance level', alpha);
  requireDegreesOfFreedom(df);
  return invertDecreasingTail((t) => studentTTwoSidedTail(t, df), alpha);
};

/** Student-t quantile: the t with P(T ≤ t) = p, for 0 < p < 1. */
export const studentTQuantile = (p: number, df: number): number => {
  requireProbability('Probability', p);
  if (p === 0.5) return 0;
  return p > 0.5
    ? twoSidedStudentTCriticalValue(2 * (1 - p), df)
    : -twoSidedStudentTCriticalValue(2 * p, df);
};

// ============================================================================
// Outlier-test helpers
// ============================================================================

/**
 * Two-sided critical value of Pope's tau statistic.
 *
 * tau = v / (σ̂₀ √q_vv) uses the a-posteriori σ̂₀ estimated from the same
 * residuals, so |tau| ≤ √r. tau² / r follows Beta(1/2, (r − 1)/2), which
 * gives the closed form below in terms of the Student-t quantile with
 * r − 1 degrees of freedom. As r → ∞ it approaches the normal critical
 * value used by Baarda's w-test with known σ₀.
 *
 * @param alpha - two-sided significance level per tested component
 * @param redundancy - degrees of freedom r of the adjustment; must exceed 1
 */
export const tauCriticalValue = (alpha: number, redundancy: number): number => {
  if (!Number.isFinite(redundancy) || redundancy <= 1) {
    throw new RangeError(
      `The tau test needs redundancy greater than 1; got ${redundancy}`
    );
  }
  const t = twoSidedStudentTCriticalValue(alpha, redundancy - 1);
  return (t * Math.sqrt(redundancy)) / Math.sqrt(redundancy - 1 + t * t);
};

/** Default type II error β for minimal detectable bias (power 1 − β = 80%). */
export const DEFAULT_BETA = 0.2;

/**
 * Baarda's non-centrality δ₀ = z(1 − α/2) + z(1 − β): the shift, in units of
 * a residual's standard deviation, that a two-sided test at level α detects
 * with probability 1 − β (known σ₀). δ₀ ≈ 4.13 for α = 0.001, β = 0.2.
 */
export const baardaNoncentrality = (
  alpha: number,
  beta: number = DEFAULT_BETA
): number => {
  requireProbability('Type II error beta', beta);
  return twoSidedNormalCriticalValue(alpha) + normalQuantile(1 - beta);
};
